/**
 * Affiliate Click Tracking Utility
 * Tracks outbound affiliate clicks via GA4 and PostHog
 */
import { API } from "@/utils/constants";
import { logger } from "./logger";

/**
 * Universal affiliate link resolver.
 * Prefers V2 (firm.affiliate.link), falls back to V1 (firm.affiliate_link).
 * Returns UTM-tagged URL or null if no link available.
 */
export const getAffiliateLink = (firm) => {
  if (!firm) return null;

  // Resolve raw URL: V2 first, V1 fallback
  const v2 = firm.affiliate?.link;
  const v1 = firm.affiliate_link;
  const raw = (v2 && v2.startsWith("http")) ? v2 : (v1 && v1.startsWith("http")) ? v1 : null;

  if (!raw) {
    logger.error(`[getAffiliateLink] Missing affiliate URL for firm: ${firm.name || firm.slug || "unknown"}`);
    return null;
  }

  // Apply UTM (safe — appendUTM prevents duplicates)
  return appendUTM(raw, firm.slug);
};

/**
 * Append UTM parameters to an affiliate link.
 * - Parses URL properly (handles existing params, hash fragments)
 * - Detects existing utm_source — does NOT duplicate
 * - Preserves all existing tracking parameters
 */
export const appendUTM = (url, firmSlug) => {
  if (!url) return url;

  try {
    // Split hash fragment if present
    const [urlPart, hash] = url.split("#", 2);
    const hashSuffix = hash ? `#${hash}` : "";

    // Parse existing query params
    const qIndex = urlPart.indexOf("?");
    const base = qIndex >= 0 ? urlPart.slice(0, qIndex) : urlPart;
    const queryString = qIndex >= 0 ? urlPart.slice(qIndex + 1) : "";
    const params = new URLSearchParams(queryString);

    // Check for existing UTM — do not duplicate
    if (params.has("utm_source")) {
      if (typeof console !== "undefined") {
        logger.warn(`[appendUTM] Skipped: utm_source already present on ${url.slice(0, 60)}`);
      }
      return url;
    }

    // Append UTM params
    params.set("utm_source", "futuresfury");
    params.set("utm_medium", "affiliate");
    params.set("utm_campaign", firmSlug || "unknown");

    return `${base}?${params.toString()}${hashSuffix}`;
  } catch {
    // Fallback: return original URL unmodified
    return url;
  }
};

export const trackAffiliateClick = (firmName, firmSlug, affiliateLink, location = 'unknown') => {
  // GA4 Event
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'affiliate_click', {
      event_category: 'Affiliate',
      event_label: firmName,
      firm_name: firmName,
      firm_slug: firmSlug,
      cta_position: location,
      page_path: window.location.pathname,
      destination_url: affiliateLink,
    });
  }

  // PostHog Event
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture('affiliate_click', {
      firm_name: firmName,
      firm_slug: firmSlug,
      cta_position: location,
      page_path: window.location.pathname,
      destination_url: affiliateLink,
    });
  }

  // Console log for debugging
  void 0; // affiliate click tracked silently

  // Persist to backend (fire-and-forget)
  try {
    fetch(`${API}/analytics/click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        firm_slug: firmSlug || "",
        firm_name: firmName || "",
        page_path: typeof window !== "undefined" ? window.location.pathname : "",
        placement: location || "unknown",
      }),
    }).catch((err) => { if (process.env.NODE_ENV === 'development') logger.error('[tracking] affiliate click failed:', err); });
  } catch (err) { if (process.env.NODE_ENV === 'development') logger.error('[tracking] affiliate click error:', err); }
};

/**
 * Affiliate Link Component Props Handler
 * Use this to wrap affiliate link clicks
 */
export const handleAffiliateClick = (firm, location) => {
  return () => {
    trackAffiliateClick(
      firm.name,
      firm.slug,
      firm.affiliate_link,
      location
    );
  };
};
